import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { createPaymentIntent, confirmPayment, createFreeMembership } from '../../services/api';

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

const defaultBillingDetails = {
  name: '',
  email: '',
  address: {
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  }
};

const PaymentFormContent = ({
  plan,
  currentMembership,
  onSuccess,
  onError,
  onFailure,
  autoNavigateOnSuccess = true,
  initialBilling
}) => {
  const planPrice = Number(plan.price) || 0;

  console.log('💳 PaymentFormContent rendered with plan:', {
    id: plan.id,
    name: plan.name,
    price: plan.price,
    planPrice: planPrice,
    isFree: planPrice === 0
  });

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState(() => {
    const base = {
      name: defaultBillingDetails.name,
      email: defaultBillingDetails.email,
      address: { ...defaultBillingDetails.address }
    };

    if (!initialBilling) {
      return base;
    }

    return {
      name: initialBilling.name || '',
      email: initialBilling.email || '',
      address: {
        line1: initialBilling.address?.line1 || '',
        city: initialBilling.address?.city || '',
        state: initialBilling.address?.state || '',
        postal_code: initialBilling.address?.postal_code || '',
        country: initialBilling.address?.country || 'US'
      }
    };
  });

  const buildBillingMetadata = () => ({
    name: billingDetails.name,
    email: billingDetails.email,
    address_line1: billingDetails.address.line1,
    city: billingDetails.address.city,
    state: billingDetails.address.state,
    postal_code: billingDetails.address.postal_code,
    country: billingDetails.address.country
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    const planPrice = Number(plan.price) || 0;

    console.log('💳 PaymentForm handleSubmit called', {
      planPrice: planPrice,
      isFree: planPrice === 0,
      planName: plan.name
    });

    if (!stripe || !elements) {
      console.log('💳 Stripe or elements not ready');
      return;
    }

    setLoading(true);
    setError('');
    setProcessing(true);

    try {
      // Handle free membership separately
      if (planPrice === 0) {
        console.log('💳 Processing free membership signup');
        const result = await createFreeMembership({
          membership_type_id: plan.id
        });

        console.log('💳 Free membership result:', result);

        if (result.success) {
          console.log('💳 Free membership successful, calling onSuccess');
          onSuccess(result);
          if (autoNavigateOnSuccess) {
            console.log('💳 Navigating to dashboard');
            navigate('/dashboard', {
              state: {
                message: `Welcome! You've successfully joined as a ${plan.name}.`
              }
            });
          }
        } else {
          console.error('💳 Free membership failed:', result);
        }
        return;
      }

      // Create payment intent for paid plans
      const { client_secret, payment_id } = await createPaymentIntent({
        membership_type_id: plan.id,
        billing_metadata: buildBillingMetadata()
      });

      // Confirm payment with Stripe
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: billingDetails.name,
            email: billingDetails.email,
            address: billingDetails.address,
          },
        }
      });

      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
        if (onFailure) {
          onFailure(result.error.message);
        }
        if (onError) {
          onError(result.error.message);
        }
      } else {
        // Payment succeeded, confirm with backend
        const confirmResult = await confirmPayment({
          payment_intent_id: result.paymentIntent.id,
          payment_id: payment_id
        });

        if (confirmResult.success) {
          onSuccess(confirmResult);
          if (autoNavigateOnSuccess) {
            navigate('/dashboard', {
              state: {
                message: `Payment successful! You're now a ${plan.name}.`
              }
            });
          }
        } else {
          setError('Payment confirmation failed. Please contact support.');
          if (onFailure) {
            onFailure('Payment confirmation failed. Please contact support.');
          }
          if (onError) {
            onError('Payment confirmation failed. Please contact support.');
          }
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred during payment processing.');
      if (onFailure) {
        onFailure(err.message);
      }
      if (onError) {
        onError(err.message);
      }
    } finally {
      setLoading(false);
      setProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBillingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  console.log('💳 PaymentForm rendering section:', {
    planPrice: planPrice,
    isFree: planPrice === 0,
    willShowFreeForm: planPrice === 0
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {planPrice === 0 ? (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Free Membership</h3>
          <p className="text-gray-600 mb-4">You're signing up for a free Community membership.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Billing Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Billing Information</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={billingDetails.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={billingDetails.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your.email@domain.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
                <input
                  type="text"
                  value={billingDetails.address.line1}
                  onChange={(e) => handleInputChange('address.line1', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Main Street"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={billingDetails.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="New York"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    value={billingDetails.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="NY"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                <input
                  type="text"
                  value={billingDetails.address.postal_code}
                  onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10001"
                  required
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Payment Information</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <CardNumberElement options={cardStyle} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
                  <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <CardExpiryElement options={cardStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Security Code</label>
                  <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <CardCvcElement options={cardStyle} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {planPrice > 0 && (
          <p className="text-sm text-gray-500 text-center">
            * All fields are required
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!stripe || loading || processing}
            className="px-6 py-3 border border-transparent rounded-md shadow-sm text-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {processing ? 'Processing...' : planPrice === 0 ? 'Join Free' : 'Subscribe'}
          </button>
        </div>
      </div>
    </form>
  );
};

const PaymentForm = ({
  plan,
  currentMembership,
  onSuccess,
  onError,
  onFailure,
  autoNavigateOnSuccess = true,
  initialBilling
}) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent
        plan={plan}
        currentMembership={currentMembership}
        onSuccess={onSuccess}
        onError={onError}
        onFailure={onFailure}
        autoNavigateOnSuccess={autoNavigateOnSuccess}
        initialBilling={initialBilling}
      />
    </Elements>
  );
};

export default PaymentForm;
